import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TenantProvider, useTenant } from './context/TenantContext';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Properties from './pages/Properties';
import Leads from './pages/Leads';
import Chat from './pages/Chat';
import Clients from './pages/Clients';
import Appointments from './pages/Appointments';
import Branding from './pages/Branding';
import Knowledge from './pages/Knowledge';
import ListingFiles from './pages/ListingFiles';
import Resources from './pages/Resources';
import Layout from './components/Layout';
import BuyerChat from './pages/BuyerChat';
import Legal from './pages/Legal';
import { ClientArea, BuyerChatWithClientAuth } from './components/ClientArea';
import './App.css';

const RESERVED_PATH_SLUGS = new Set([
  'login', 'signup', 'privacy', 'terms', 'buyer', 'chat', 'client',
  'properties', 'leads', 'clients', 'appointments', 'branding',
  'knowledge', 'listing-files', 'resources', 'dashboard', 'api', 'health', 'embed',
  'admin', 'static', 'assets', 'media', 'onboarding', 'settings',
  'billing',
]);

function LoadingScreen() {
  return <div className="loading-screen"><div className="spinner" /></div>;
}

function RootGate() {
  const { agent, loading } = useAuth();
  const tenant = useTenant();
  if (tenant.loading || loading) return <LoadingScreen />;
  if (tenant.agent) {
    return <BuyerChatWithClientAuth><BuyerChat /></BuyerChatWithClientAuth>;
  }
  if (!agent) return <Navigate to="/chat/sarah-murphy" replace />;
  return <Layout />;
}

function PathTenantChat() {
  const { slug: agentSlug } = useParams();
  const tenant = useTenant();
  const slug = String(agentSlug || '').trim().toLowerCase();
  if (tenant.loading) return <LoadingScreen />;
  if (tenant.agent || !tenant.pathTenantRoutesEnabled || !slug || RESERVED_PATH_SLUGS.has(slug)) {
    return <Navigate to="/" replace />;
  }
  return <BuyerChatWithClientAuth><BuyerChat /></BuyerChatWithClientAuth>;
}

function App() {
  return (
    <BrowserRouter>
      <TenantProvider>
        <AuthProvider>
          <Toaster position="top-right" toastOptions={{
            style: { background: '#ffffff', color: '#151b2b', border: '1px solid #dbe2ee' }
          }} />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/privacy" element={<Legal />} />
            <Route path="/terms" element={<Legal />} />
            <Route path="/buyer" element={<BuyerChatWithClientAuth><BuyerChat /></BuyerChatWithClientAuth>} />
            <Route path="/chat/:slug/conversation/:conversationId" element={<BuyerChatWithClientAuth><BuyerChat /></BuyerChatWithClientAuth>} />
            <Route path="/chat/:slug" element={<BuyerChatWithClientAuth><BuyerChat /></BuyerChatWithClientAuth>} />
            <Route path="/client/:slug/*" element={<ClientArea />} />
            <Route path="/" element={<RootGate />}>
              <Route index element={<Dashboard />} />
              <Route path="properties" element={<Properties />} />
              <Route path="leads" element={<Leads />} />
              <Route path="clients" element={<Clients />} />
              <Route path="chat" element={<Chat />} />
              <Route path="appointments" element={<Appointments />} />
              <Route path="branding" element={<Branding />} />
              <Route path="knowledge" element={<Knowledge />} />
              <Route path="listing-files" element={<ListingFiles />} />
              <Route path="resources" element={<Resources />} />
            </Route>
            <Route path="/:slug" element={<PathTenantChat />} />
          </Routes>
        </AuthProvider>
      </TenantProvider>
    </BrowserRouter>
  );
}

export default App;
